/**
 * 来場者画面の効果音再生ユーティリティ
 * 成功演出に対応する音声をブラウザ上で再生します
 */

import { logWarn } from './logger';

/** 来場者画面で再生する効果音の種類 */
export const VISITOR_SOUND_NAMES = Object.freeze({
  confirm: 'confirm',
  pointClaim: 'pointClaim',
} as const);

/** 効果音名 */
export type VisitorSoundName = typeof VISITOR_SOUND_NAMES[keyof typeof VISITOR_SOUND_NAMES];

/** 効果音ファイルの公開パス */
const VISITOR_SOUND_PATHS: Record<VisitorSoundName, string> = Object.freeze({
  confirm: '/audio/confirm.mp3',
  pointClaim: '/audio/point-get.mp3',
});

/** 再利用する音声要素のキャッシュ */
const audioCache = new Map<VisitorSoundName, HTMLAudioElement>();

/**
 * 効果音の音声要素を取得する
 * @param {VisitorSoundName} soundName - 効果音名
 * @returns {HTMLAudioElement | null} 音声要素
 */
const getAudioElement = (soundName: VisitorSoundName) => {
  if (typeof window === 'undefined' || typeof window.Audio !== 'function') {
    return null;
  }

  const cachedAudio = audioCache.get(soundName);

  if (cachedAudio) {
    return cachedAudio;
  }

  /** 新しく作成する音声要素 */
  const audio = new window.Audio(VISITOR_SOUND_PATHS[soundName]);
  audio.preload = 'auto';
  audioCache.set(soundName, audio);

  return audio;
};

/**
 * 来場者画面の効果音を再生する
 * 音声再生がブラウザに拒否されても、成功処理や画面表示は継続します
 * @param {VisitorSoundName} soundName - 効果音名
 * @returns {void}
 */
export const playVisitorSound = (soundName: VisitorSoundName) => {
  const audio = getAudioElement(soundName);

  if (!audio) {
    return;
  }

  audio.currentTime = 0;
  const playPromise = audio.play();

  if (playPromise && typeof playPromise.catch === 'function') {
    void playPromise.catch((error) => {
      logWarn('効果音の再生に失敗しました。', {
        errorMessage: error instanceof Error ? error.message : String(error),
        soundName,
      });
    });
  }
};
